import { useEffect, useState } from "react";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { DashboardFilterType, ProductRatio } from "@/types/dashboard";

import { getDashboardBidFilterToString } from "@/consts/Dashboard";

import { GetMonthlyBidNoticeContractAmountStats } from "@/services/Dashboard";

import { showToast } from "@/utils/toastUtils";

import DashboardCardHeader from "@/components/DashboardCardHeader";
import { formatKoreanCurrencyUnits } from "@/libs/utils";

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-white border border-gray-200 p-2 rounded shadow text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((entry: any, index: number) => (
        <p key={index} style={{ color: entry.color }} className="leading-4">
          {Number(entry.value).toLocaleString()}
        </p>
      ))}
    </div>
  );
};

type Props = {
  title: string;
  searchReady: boolean;
  filter: DashboardFilterType;
  onSearchComplete: () => void;
};

export default function DashboardSingleBarChart({
  title,
  searchReady,
  filter,
  onSearchComplete,
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<ProductRatio[]>([]);

  const onLoad = async () => {
    try {
      setIsLoading(true);
      const params = getDashboardBidFilterToString(filter);
      const result = await GetMonthlyBidNoticeContractAmountStats(params);
      if (result.data.success) {
        const newData = result.data.pods[0].content.items.map((item) => ({
          label: `${String(item.year).substring(2)}.${String(item.month).padStart(2, "0")}`,
          value: item.amount,
        }));
        setData(newData);
      } else {
        showToast({
          type: "error",
          message: `${title}을(를) 조회하는데 실패했습니다. ${result.data.error.type}`,
        });
      }
    } catch (e) {
      showToast({
        type: "error",
        message: `${title}을(를) 조회하는데 실패했습니다. ${e}`,
      });
    } finally {
      setIsLoading(false);
      onSearchComplete();
    }
  };

  useEffect(() => {
    if (searchReady) {
      onLoad();
    }
  }, [searchReady]);
  return (
    <DashboardCardHeader title={title} isLoading={isLoading}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ right: 20, left: 20 }}>
          <CartesianGrid strokeDasharray="0" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 14, fontWeight: 700 }}
            tickLine={false}
          />
          <YAxis
            domain={[0, "dataMax"]}
            tick={{ fontSize: 14, fontWeight: 700 }}
            axisLine={false}
            tickLine={false}
            width={30}
            allowDuplicatedCategory={false}
            tickFormatter={(value) => formatKoreanCurrencyUnits(value, 1)}
          />
          <Tooltip content={<CustomTooltip />} />

          <Bar dataKey="value" fill="#344BFD" barSize={24} radius={5} />
        </BarChart>
      </ResponsiveContainer>
    </DashboardCardHeader>
  );
}
